import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Input,
  SearchBox,
  SecondaryButton,
  TextHeadingThree,
  TextHeadingTwo,
} from '@cko/primitives';
import styled from 'styled-components';
import { useDeveloperToolsProvider } from '../../../../context';
import { GridView, GridViewContent } from '../../../GridView';
import { filterBySearch } from '../utils';
import { FlagToggle } from './FlagToggle';

const getFirstTwoWords = (str: string) => {
  // Match sequences of capital letters followed by lowercase letters
  const words = str.match(/[A-Z]?[a-z]+/g);
  // Combine the first two words if they exist
  if (!words) {
    return '';
  }
  return words.slice(0, 2).join('');
};

const Container = styled.div`
  display: flex, 
  flex-direction: column,
  gap: var(--layout-soybean)
`;
const SearchBoxContainer = styled.div`
  width: 400px;
`;

const AddNewFlagContainer = styled.div`
  display: grid;
  grid-template-columns: 300px 50px 50px;
  gap: var(--layout-soybean);
  align-items: center;
`;

export const FeatureFlagsPanel = () => {
  const { overrides, setOverrides, showOverridesOnly } = useDeveloperToolsProvider();
  const [newFlagName, setNewFlagName] = useState('');
  const [searchString, setSearchString] = useState<string | undefined>('');
  const flags = useFlags();

  const toggleFlag = (flagName: string) => {
    const hasLocalOverride = flagName in overrides.featureFlags;
    const currentValue: boolean = hasLocalOverride
      ? overrides.featureFlags[flagName]
      : Boolean(flags[flagName]); // we dont currently use the other data types from launch darkly
    const newFeatureFlagsOverrides = { ...overrides.featureFlags };

    if (hasLocalOverride) {
      delete newFeatureFlagsOverrides[flagName];
    } else {
      newFeatureFlagsOverrides[flagName] = !currentValue;
    }

    setOverrides({
      ...overrides,
      featureFlags: newFeatureFlagsOverrides,
    });
  };

  const handleAddFlag = () => {
    if (newFlagName.trim()) {
      setOverrides({
        ...overrides,
        featureFlags: { ...overrides.featureFlags, [newFlagName]: true },
      });
    }
  };

  const flagsToDisplay = filterBySearch(
    showOverridesOnly ? overrides.featureFlags : { ...flags, ...overrides.featureFlags },
    searchString,
  );

  const flagPrefixes = Object.entries(flagsToDisplay)
    .map(([key]) => getFirstTwoWords(key))
    .filter((key, index, array) => array.indexOf(key) === index)
    .sort();

  const gridViewContent = flagPrefixes.map((prefix: string) => {
    const matchingFlags = Object.entries(flagsToDisplay).filter(([key]) => key.startsWith(prefix));
    return (
      <GridViewContent key={prefix} title={prefix}>
        {matchingFlags.map(([flagName, enabled]) => {
          return (
            <FlagToggle
              key={flagName}
              label={flagName}
              value={enabled}
              initialValue={flags[flagName]}
              onToggle={() => toggleFlag(flagName)}
            />
          );
        })}
      </GridViewContent>
    );
  });

  return (
    <Container>
      <TextHeadingTwo>Current Flags</TextHeadingTwo>
      <SearchBoxContainer>
        <SearchBox
          aria-label="Search"
          placeholder="Search for a feature flag"
          onChange={setSearchString}
          onClear={() => setSearchString(undefined)}
          onSearch={() => {}}
        />
      </SearchBoxContainer>
      <GridView noOfColumns={2}>{gridViewContent}</GridView>
      <TextHeadingThree>Add New Flag</TextHeadingThree>
      <AddNewFlagContainer>
        <div style={{ width: '290px' }}>
          <Input
            label=""
            isRequired
            onChange={(e) => setNewFlagName(e.target.value)}
            placeholder="Feature flag name"
            value={newFlagName}
          />
        </div>

        <SecondaryButton onClick={handleAddFlag}>Add</SecondaryButton>
      </AddNewFlagContainer>
    </Container>
  );
};
